import { Controller } from "@hotwired/stimulus";

/**
 * Clone Options Controller
 *
 * Drives the progressive disclosure on the clone page: the slug follows the
 * title until it is edited, the moved dates recompute from the chosen start,
 * the option picker appears only when the organiser asks to choose, and Create
 * stays inactive until the note is accepted.
 *
 * UX only. Event::CloneEvent::Options is the source of truth for what actually
 * gets copied, and re-checks every dependency server side.
 */

// Mirrors Event::CloneEvent::Options::DEPENDENCIES.
const DEPENDENCIES = {
  reports: ["tracks", "sessions"],
  pdf_templates: ["tracks", "reports"],
  confirmation_messages: ["tracks"],
  email_campaigns: ["tracks", "reports", "pdf_templates"],
  push_notifications: ["tracks", "reports", "pdf_templates"],
  cancellation_policies: ["tracks"],
};

const LABELS = {
  microsite: "the event site",
  tracks: "tracks",
  sessions: "sessions",
  reports: "saved reports",
  sponsors: "sponsor tiers",
  showcase: "showcase",
  pdf_templates: "PDF templates",
  confirmation_messages: "confirmation messages",
  email_campaigns: "email campaigns",
  push_notifications: "push notifications",
  cancellation_policies: "cancellation policies",
  discount_codes: "discount codes",
  schedule: "the schedule",
  room_blocks: "room blocks",
};

const DAY_MS = 86400000;

// Whole days between two local calendar dates, times discarded. Built from UTC
// midnights so no DST transition can make a day 23 or 25 hours long.
const calendarDaysBetween = (from, to) => {
  const day = (value) => {
    const [date] = String(value).split("T");
    const [y, m, d] = date.split("-").map(Number);
    return Date.UTC(y, m - 1, d);
  };

  return Math.round((day(to) - day(from)) / DAY_MS);
};

// YYYY-MM-DD from the local calendar date, which is what a date input holds.
const localDateValue = (date) =>
  [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");

// Entity labels are written for mid-sentence use; button labels are Title Case.
const LOWER_IN_TITLE = ["and", "the"];

const titleCase = (text) =>
  text
    .split(" ")
    .map((word, index) =>
      index > 0 && LOWER_IN_TITLE.includes(word) ? word : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");

export class CloneOptionsController extends Controller {
  static targets = [
    "title",
    "slug",
    "startAt",
    "endAt",
    "reviewDates",
    "dateFields",
    "mappedDate",
    "picker",
    "integrationsWarning",
    "entity",
    "onCount",
    "rateWarning",
    "sponsorSectionsWarning",
    "dependencyWarning",
    "dependencyTitle",
    "dependencyMessage",
    "dependencyFix",
    "dependencyDrop",
    "acknowledge",
    "submit",
  ];

  connect() {
    // Disabled here rather than in the template. Rendered disabled, a Stimulus
    // failure left the page permanently unsubmittable; enabled by default, the
    // worst case without JavaScript is that the server rejects an
    // unacknowledged submission and says so.
    if (this.hasSubmitTarget) this.submitTarget.disabled = true;
    this.slugTouched = false;
    this.baselineStart = this.hasStartAtTarget ? this.startAtTarget.value : null;
    this.baselineDates = this.mappedDateTargets.map((el) => el.value);
    this.entityChanged();
    this.toggleAcknowledge();
  }

  // The slug follows the title until someone edits it, then it is theirs.
  titleChanged() {
    if (this.slugTouched || !this.hasSlugTarget || !this.hasTitleTarget) return;

    this.slugTarget.value = this.titleTarget.value
      .toLowerCase()
      .trim()
      .replace(/['’]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  slugEdited() {
    this.slugTouched = true;
  }

  toggleReviewDates() {
    if (!this.hasDateFieldsTarget || !this.hasReviewDatesTarget) return;

    this.dateFieldsTarget.hidden = !this.reviewDatesTarget.checked;
  }

  // Touching a date takes it off the offset: from here it is the organiser's
  // value and stops following the event start.
  dateEdited(event) {
    event.target.dataset.cloneOptionsEdited = "true";
  }

  // Opening "Review all dates" must not pin anything. The fields are prefilled
  // with what the offset produces, so submitting them all would send every date
  // back as a hand-set override and the server would stop moving them. Only the
  // ones actually touched are submitted.
  pruneUntouchedDates() {
    this.mappedDateTargets.forEach((el) => {
      if (el.dataset.cloneOptionsEdited !== "true") el.disabled = true;
    });
  }

  // Move the revealed dates by the same offset the server will apply, so the
  // fields never show a date the clone will not actually use.
  datesChanged() {
    if (!this.hasStartAtTarget || !this.baselineStart) return;

    const chosen = this.startAtTarget.value;
    if (!chosen) return;

    // Calendar days between the two dates, ignoring the time of day. The field
    // is datetime-local now, so subtracting the raw values and rounding turned
    // a start moved to the evening of the same day into a whole day of drift,
    // while the server measured zero. This mirrors DateMapper#offset_between,
    // which subtracts two dates rather than two instants.
    const offset = calendarDaysBetween(this.baselineStart, chosen);

    this.mappedDateTargets.forEach((el, index) => {
      const baseline = this.baselineDates[index];
      if (!baseline || el.dataset.cloneOptionsEdited === "true") return;

      // Shifted through UTC midnight for the same reason: adding milliseconds to
      // a local instant lands on the previous day across a DST boundary.
      const [by, bm, bd] = baseline.split("-").map(Number);
      const shifted = new Date(Date.UTC(by, bm - 1, bd) + offset * DAY_MS);
      // Formatted from the local parts, not through toISOString, which converts
      // to UTC and shifted every revealed date back a day for anyone at a
      // positive offset.
      el.value = localDateValue(shifted);
    });
  }

  scopeChanged(event) {
    const choosing = event.target.value === "some";

    if (this.hasPickerTarget) this.pickerTarget.hidden = !choosing;
    // The integrations caution belongs to the All scope: under Select Options
    // the row carries its own caption and the organiser can untick it.
    if (this.hasIntegrationsWarningTarget) this.integrationsWarningTarget.hidden = choosing;
  }

  entityChanged() {
    this.updateCount();
    this.updateRateWarning();
    this.updateSponsorSectionsWarning();
    this.updateDependencyWarning();
  }

  updateCount() {
    if (!this.hasOnCountTarget) return;

    const on = this.selectedKeys().length;
    const total = this.entityTargets.length;
    // "2 of 2" asks the reader to do arithmetic to learn nothing is excluded.
    this.onCountTarget.textContent = on === total ? `All ${total} selected` : `${on} of ${total} selected`;
  }

  // Only shown once room blocks are actually being copied, since it is about
  // prices coming across rather than about the option existing.
  updateRateWarning() {
    if (!this.hasRateWarningTarget) return;

    this.rateWarningTarget.hidden = !this.selectedKeys().includes("room_blocks");
  }

  // The inverse of the rate warning: this one is about what leaving the option
  // OFF does to the copied site, so it appears when sponsors are unticked.
  updateSponsorSectionsWarning() {
    if (!this.hasSponsorSectionsWarningTarget) return;

    this.sponsorSectionsWarningTarget.hidden = this.selectedKeys().includes("sponsors");
  }

  updateDependencyWarning() {
    if (!this.hasDependencyWarningTarget) return;

    const selected = this.selectedKeys();
    this.pendingDependency = null;

    // GR-3464: only rows that exist on this page. The picker renders a row per
    // thing the event actually has, so requiring one that was never rendered
    // put up a banner whose Turn Them On had no checkbox to tick and whose
    // Leave It Behind could not clear it either.
    const offered = this.entityTargets.map((el) => el.dataset.cloneOptionsKeyParam);

    for (const [dependent, required] of Object.entries(DEPENDENCIES)) {
      if (!selected.includes(dependent)) continue;

      const missing = required.filter((key) => offered.includes(key) && !selected.includes(key));
      if (!missing.length) continue;

      this.pendingDependency = { dependent, missing };
      break;
    }

    this.dependencyWarningTarget.hidden = !this.pendingDependency;
    if (!this.pendingDependency) return;

    const { dependent, missing } = this.pendingDependency;
    const missingLabels = missing.map((key) => LABELS[key] || key).join(" and ");

    if (this.hasDependencyTitleTarget) {
      this.dependencyTitleTarget.textContent = `Your ${LABELS[dependent] || dependent} need ${missingLabels}`;
    }
    if (this.hasDependencyMessageTarget) {
      this.dependencyMessageTarget.textContent =
        `They filter by ${missingLabels}, so they will arrive with nothing to filter on.`;
    }
    // "Turn Them On" and "Leave It Behind" sit side by side but refer to
    // different things, so each button names what it acts on.
    if (this.hasDependencyFixTarget) {
      this.dependencyFixTarget.textContent = `Turn ${titleCase(missingLabels)} On`;
    }
    if (this.hasDependencyDropTarget) {
      this.dependencyDropTarget.textContent = `Leave ${titleCase(LABELS[dependent] || dependent)} Behind`;
    }
  }

  fixDependency() {
    if (!this.pendingDependency) return;

    this.pendingDependency.missing.forEach((key) => {
      const input = this.entityTargets.find((el) => el.dataset.cloneOptionsKeyParam === key);
      if (input) input.checked = true;
    });
    this.entityChanged();
  }

  dropDependent() {
    if (!this.pendingDependency) return;

    const input = this.entityTargets.find(
      (el) => el.dataset.cloneOptionsKeyParam === this.pendingDependency.dependent,
    );
    if (input) input.checked = false;
    this.entityChanged();
  }

  toggleAcknowledge() {
    if (!this.hasSubmitTarget || !this.hasAcknowledgeTarget) return;

    const accepted = this.acknowledgeTarget.checked;
    this.submitTarget.disabled = !accepted;
  }

  selectedKeys() {
    return this.entityTargets
      .filter((input) => input.checked)
      .map((input) => input.dataset.cloneOptionsKeyParam);
  }
}

export default CloneOptionsController;
